import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useMediaQuery, useTheme as useMuiTheme } from '@mui/material'
import {
  usePublicClient,
  useWaitForTransactionReceipt,
  useChainId,
  useChains,
  useAccount,
} from 'wagmi'
import { getEnv } from '@/config/env'
import { useNotification } from '@/hooks/useNotification'
import { isUserRejectedError } from '@/utils/errorHandler'
import TransactionCancelledModal from '../TransactionCancelledModal'
import useSafe from '@/localsafe.eth/hooks/useSafe'
import Safe, { EthSafeTransaction } from '@safe-global/protocol-kit'
import {
  submitSafeTransactionToService,
  fetchPendingTransactionsFromService,
  simulateTransaction,
} from '@/utils/safeTransactionService'
import { useSafeWalletContext } from '@/localsafe.eth/provider/SafeWalletContext'
import {
  getMinimalEIP1193Provider,
  createConnectionConfig,
} from '@/localsafe.eth/utils/helpers'
import { getAddress } from 'viem'
import { POLLING_INTERVAL_PENDING_TRANSACTIONS } from '@/constants/main'
import { getTransactionType } from './Actions/utils'
import type { PendingTransaction } from './Actions/types'
import PendingTransactions from './Actions/PendingTransactions'
import { copyToClipboard } from '@/utils/clipboard'
import { useWallet } from '@/hooks/useWallet'
import { getDemoPrivateKey } from '@/utils/demoWallet'
import { getRpcUrlForChainId } from '@/config/env'

export default function PendingTransactionsBlock() {
  const { showSuccess, showError } = useNotification()
  const muiTheme = useMuiTheme()
  const isMobileOrTablet = useMediaQuery(muiTheme.breakpoints.down('lg'))
  const { stngProxy, treasurySafe, operationalSafeFactory } = useMemo(
    () => getEnv(),
    []
  )
  const publicClient = usePublicClient()
  const { connector } = useAccount()
  const { address: userAddress } = useWallet()
  const chainId = useChainId()
  const chains = useChains()
  const chain = useMemo(
    () => chains.find((item) => item.id === chainId),
    [chains, chainId]
  )
  const demoPrivateKey = getDemoPrivateKey()
  const getSafeProviderAndSigner = useCallback(
    async () => {
      if (!userAddress || !chain?.id) {
        return null
      }

      const provider = demoPrivateKey
        ? getRpcUrlForChainId(chain.id)
        : await getMinimalEIP1193Provider(connector)
      const signer = demoPrivateKey ? demoPrivateKey : getAddress(userAddress)

      if (!provider) {
        return null
      }

      return { provider, signer }
    },
    [userAddress, chain, demoPrivateKey, connector]
  )

  const { getAllSafes, contractNetworks } = useSafeWalletContext()

  const {
    isReady: isSafeReady,
    signSafeTransaction,
    executeSafeTransaction,
    getSafeTransactionHash,
    getSafeThreshold,
    getSafeNonce,
  } = useSafe(treasurySafe as `0x${string}`)

  const [pendingTransactions, setPendingTransactions] = useState<
    PendingTransaction[]
  >([])
  const [signingTxHash, setSigningTxHash] = useState<string | null>(null)
  const [executingTxHash, setExecutingTxHash] = useState<string | null>(null)
  const [executeTxHash, setExecuteTxHash] = useState<`0x${string}` | null>(null)
  const [rejectingTxHash, setRejectingTxHash] = useState<string | null>(null)
  const [simulatingTxHash, setSimulatingTxHash] = useState<string | null>(null)
  const [simulationResults, setSimulationResults] = useState<
    Record<string, { success: boolean; error?: string }>
  >({})
  const [isLoadingPendingTransactions, setIsLoadingPendingTransactions] =
    useState(true)
  const [safeThreshold, setSafeThreshold] = useState<number | null>(null)
  const [creatingTransactions, setCreatingTransactions] = useState<
    Map<number, string>
  >(new Map())
  const [showExecuteOverlay, setShowExecuteOverlay] = useState(false)
  const [executingTxHashForOverlay, setExecutingTxHashForOverlay] = useState<
    string | null
  >(null)
  const [executingTxNonceForOverlay, setExecutingTxNonceForOverlay] = useState<
    number | null
  >(null)
  const previousTransactionsRef = useRef<PendingTransaction[]>([])
  const isInitialLoadRef = useRef<boolean>(true)
  const [showCancelledModal, setShowCancelledModal] = useState(false)
  const [cancelledMessage, setCancelledMessage] = useState<string>('')

  useEffect(() => {
    const fetchThreshold = async () => {
      if (!isSafeReady || !treasurySafe) return
      try {
        const threshold = await getSafeThreshold()
        if (threshold !== null) {
          setSafeThreshold(threshold)
        }
      } catch (error) {
        console.error('Failed to fetch Safe threshold:', error)
      }
    }
    fetchThreshold()
  }, [isSafeReady, treasurySafe, getSafeThreshold])

  const userOperationalSafes = useMemo(() => {
    if (!userAddress || !chain) return new Set<string>()
    const allSafes = getAllSafes(userAddress)
    const operationalSafes = allSafes
      .filter((safe) => safe.deployed)
      .filter(
        (safe) => safe.safeAddress.toLowerCase() !== treasurySafe?.toLowerCase()
      )
      .filter((safe) => safe.chainId === chain.id.toString())
    return new Set(
      operationalSafes.map((safe) => safe.safeAddress.toLowerCase())
    )
  }, [userAddress, chain, getAllSafes, treasurySafe])

  const removeFromCreatingTransactions = useCallback(
    (nonce: number | number[]) => {
      setCreatingTransactions((prev) => {
        const newMap = new Map(prev)
        if (Array.isArray(nonce)) {
          nonce.forEach((n) => newMap.delete(n))
        } else {
          newMap.delete(nonce)
        }
        return newMap
      })
    },
    []
  )

  useEffect(() => {
    const handleTransactionCreating = (event: Event) => {
      const customEvent = event as CustomEvent<{ nonce: number; type: string }>
      setCreatingTransactions((prev) => {
        const newMap = new Map(prev)
        newMap.set(customEvent.detail.nonce, customEvent.detail.type)
        return newMap
      })
    }

    const handleTransactionCreationFailed = (event: Event) => {
      const customEvent = event as CustomEvent<{ nonce: number }>
      removeFromCreatingTransactions(customEvent.detail.nonce)
    }

    window.addEventListener('transaction-creating', handleTransactionCreating)
    window.addEventListener(
      'transaction-creation-failed',
      handleTransactionCreationFailed
    )

    return () => {
      window.removeEventListener(
        'transaction-creating',
        handleTransactionCreating
      )
      window.removeEventListener(
        'transaction-creation-failed',
        handleTransactionCreationFailed
      )
    }
  }, [removeFromCreatingTransactions])

  const getTransactionTypeCallback = useCallback(
    async (tx: EthSafeTransaction, safeAddress: string): Promise<string> => {
      return getTransactionType(
        tx,
        safeAddress,
        stngProxy,
        operationalSafeFactory,
        publicClient
      )
    },
    [stngProxy, publicClient, operationalSafeFactory]
  )

  useEffect(() => {
    isInitialLoadRef.current = true
    setIsLoadingPendingTransactions(true)
  }, [isSafeReady, treasurySafe, chain, userAddress])

  const areTransactionsEqual = useCallback(
    (prev: PendingTransaction[], next: PendingTransaction[]): boolean => {
      if (prev.length !== next.length) {
        return false
      }

      const prevMap = new Map(
        prev.map((tx) => [
          tx.hash,
          { isReady: tx.isReady, type: tx.type, safeAddress: tx.safeAddress },
        ])
      )

      for (const nextTx of next) {
        const prevTx = prevMap.get(nextTx.hash)
        if (!prevTx) {
          return false
        }
        if (
          prevTx.isReady !== nextTx.isReady ||
          prevTx.type !== nextTx.type ||
          prevTx.safeAddress !== nextTx.safeAddress
        ) {
          return false
        }
      }

      return true
    },
    []
  )

  const loadPendingTransactions = useCallback(async () => {
    if (!isSafeReady || !treasurySafe || !chain) {
      setIsLoadingPendingTransactions(false)
      return
    }

    try {
      if (isInitialLoadRef.current) {
        setIsLoadingPendingTransactions(true)
      }

      const currentNonce = await getSafeNonce()
      const treasuryResult = await fetchPendingTransactionsFromService(
        treasurySafe,
        chain.id,
        currentNonce ?? undefined
      )

      const allTransactions: PendingTransaction[] = []

      if (treasuryResult.success && treasuryResult.transactionsWithMeta) {
        const typePromises = treasuryResult.transactionsWithMeta.map((txMeta) =>
          getTransactionTypeCallback(txMeta.tx, treasurySafe).then((type) => ({
            txMeta,
            type,
          }))
        )
        const typedTxs = await Promise.all(typePromises)

        const treasuryTxs = typedTxs.map(({ txMeta, type }) => ({
          tx: txMeta.tx,
          hash: txMeta.safeTxHash,
          isReady: txMeta.confirmationsCount >= txMeta.confirmationsRequired,
          type,
          safeAddress: treasurySafe,
          createdDate: txMeta.submissionDate,
        }))
        allTransactions.push(...treasuryTxs)
      }

      const previousTransactions = previousTransactionsRef.current
      if (!areTransactionsEqual(previousTransactions, allTransactions)) {
        setPendingTransactions(allTransactions)
        previousTransactionsRef.current = allTransactions

        removeFromCreatingTransactions(
          allTransactions.map((tx) => tx.tx.data.nonce)
        )

        if (executingTxNonceForOverlay !== null) {
          const executedTxStillPending = allTransactions.some(
            (tx) => tx.tx.data.nonce === executingTxNonceForOverlay
          )

          if (!executedTxStillPending) {
            setExecutingTxHashForOverlay(null)
            setExecutingTxNonceForOverlay(null)
            setShowExecuteOverlay(false)
          }
        }
      } else {
        setCreatingTransactions((prev) => {
          if (prev.size === 0) return prev
          const newMap = new Map(prev)
          let hasChanges = false
          prev.forEach((_type, nonce) => {
            const txInList = allTransactions.some(
              (tx) => tx.tx.data.nonce === nonce
            )
            if (txInList) {
              newMap.delete(nonce)
              hasChanges = true
            }
          })
          return hasChanges ? newMap : prev
        })
      }

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false
        setIsLoadingPendingTransactions(false)
      }
    } catch (error) {
      console.error('Failed to load pending transactions:', error)
      const emptyTransactions: PendingTransaction[] = []
      if (previousTransactionsRef.current.length > 0) {
        setPendingTransactions(emptyTransactions)
        previousTransactionsRef.current = emptyTransactions
      }
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false
        setIsLoadingPendingTransactions(false)
      }
    }
  }, [
    isSafeReady,
    treasurySafe,
    chain,
    getSafeNonce,
    getTransactionTypeCallback,
    areTransactionsEqual,
    executingTxNonceForOverlay,
    removeFromCreatingTransactions,
  ])

  useEffect(() => {
    loadPendingTransactions()
    const interval = setInterval(
      loadPendingTransactions,
      POLLING_INTERVAL_PENDING_TRANSACTIONS
    )
    return () => clearInterval(interval)
  }, [loadPendingTransactions])

  const handleExecutePendingTransaction = useCallback(
    async (
      tx: EthSafeTransaction,
      hash: string,
      safeAddress: string,
      isReady?: boolean
    ) => {
      const isTreasurySafe =
        safeAddress.toLowerCase() === treasurySafe?.toLowerCase()

      if (!isSafeReady && isTreasurySafe) {
        showError('Safe is not ready. Please wait...')
        return
      }

      if (!userAddress || !publicClient) {
        showError('Wallet not connected')
        return
      }

      if (isReady === false) {
        showError(
          'Transaction does not have enough signatures to execute. Please wait for more signatures.'
        )
        return
      }

      try {
        setExecutingTxHash(hash)
        setExecutingTxHashForOverlay(hash)
        setExecutingTxNonceForOverlay(tx.data.nonce)
        setShowExecuteOverlay(true)

        let hasEnoughSignatures = true
        if (isTreasurySafe) {
          const threshold = await getSafeThreshold()
          const signatureCount = tx.signatures?.size || 0
          hasEnoughSignatures =
            threshold !== null && signatureCount >= threshold
        } else {
          const connectionData = await getSafeProviderAndSigner()
          if (!connectionData) {
            showError('Provider not available')
            setExecutingTxHash(null)
            setExecutingTxHashForOverlay(null)
            setExecutingTxNonceForOverlay(null)
            setShowExecuteOverlay(false)
            return
          }

          const config = createConnectionConfig(
            connectionData.provider,
            connectionData.signer,
            safeAddress as `0x${string}`,
            contractNetworks
          )

          const kit = await Safe.init(config)
          const connectedKit = await kit.connect(config)
          const threshold = await connectedKit.getThreshold()
          const signatureCount = tx.signatures?.size || 0
          hasEnoughSignatures = signatureCount >= threshold
        }

        if (!hasEnoughSignatures) {
          showError(
            'Transaction does not have enough signatures to execute. Please wait for more signatures.'
          )
          setExecutingTxHash(null)
          setExecutingTxHashForOverlay(null)
          setExecutingTxNonceForOverlay(null)
          setShowExecuteOverlay(false)
          return
        }

        let txToExecute = tx
        if (chain?.id) {
          try {
            const currentNonce = await getSafeNonce()
            const result = await fetchPendingTransactionsFromService(
              safeAddress,
              chain.id,
              currentNonce ?? undefined
            )

            if (result.success && result.transactionsWithMeta) {
              const latestTx = result.transactionsWithMeta.find(
                (txMeta) => txMeta.safeTxHash === hash
              )

              if (latestTx && latestTx.tx.signatures) {
                txToExecute = latestTx.tx
              }
            }
          } catch (error) {
            console.error('Failed to reload transaction:', error)
          }
        }

        if (isTreasurySafe) {
          try {
            const connectionData = await getSafeProviderAndSigner()
            if (!connectionData) {
              showError('Provider not available')
              setExecutingTxHash(null)
              setShowExecuteOverlay(false)
              return
            }

            const config = createConnectionConfig(
              connectionData.provider,
              connectionData.signer,
              safeAddress as `0x${string}`,
              contractNetworks
            )

            const kit = await Safe.init(config)
            const connectedKit = await kit.connect(config)

            const computedHash = await connectedKit.getTransactionHash(
              txToExecute
            )

            if (computedHash.toLowerCase() !== hash.toLowerCase()) {
              showError(
                'Transaction hash mismatch. Please reload the transaction.'
              )
              setExecutingTxHash(null)
              setExecutingTxHashForOverlay(null)
              setExecutingTxNonceForOverlay(null)
              setShowExecuteOverlay(false)
              return
            }

            const owners = await connectedKit.getOwners()
            const signatureSigners = Array.from(
              txToExecute.signatures?.keys() || []
            )
            const invalidSigners = signatureSigners.filter(
              (signer) =>
                !owners.some(
                  (owner) => owner.toLowerCase() === signer.toLowerCase()
                )
            )

            if (invalidSigners.length > 0) {
              showError(
                `Transaction has signatures from invalid owners: ${invalidSigners.join(
                  ', '
                )}`
              )
              setExecutingTxHash(null)
              setExecutingTxHashForOverlay(null)
              setExecutingTxNonceForOverlay(null)
              setShowExecuteOverlay(false)
              return
            }
          } catch (error) {
            console.error('Failed to validate transaction:', error)
          }
        }

        let executedHash: `0x${string}` | null = null

        if (isTreasurySafe) {
          executedHash = await executeSafeTransaction(txToExecute)
        } else {
          const connectionData = await getSafeProviderAndSigner()
          if (!connectionData) {
            showError('Provider not available')
            setExecutingTxHash(null)
            setExecutingTxHashForOverlay(null)
            setExecutingTxNonceForOverlay(null)
            setShowExecuteOverlay(false)
            return
          }

          const config = createConnectionConfig(
            connectionData.provider,
            connectionData.signer,
            safeAddress as `0x${string}`,
            contractNetworks
          )

          const kit = await Safe.init(config)
          const connectedKit = await kit.connect(config)

          const result = await connectedKit.executeTransaction(txToExecute)
          executedHash = result.hash as `0x${string}`
        }

        if (executedHash) {
          setExecuteTxHash(executedHash)
          showSuccess(
            'Transaction execution submitted! Waiting for confirmation...'
          )
        }
      } catch (error) {
        setExecutingTxHash(null)
        if (isUserRejectedError(error)) {
          setCancelledMessage(
            'You cancelled the execution transaction in MetaMask.'
          )
          setShowCancelledModal(true)
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to execute transaction'
          )
        }
        setExecutingTxHashForOverlay(null)
        setExecutingTxNonceForOverlay(null)
        setShowExecuteOverlay(false)
      }
    },
    [
      isSafeReady,
      treasurySafe,
      executeSafeTransaction,
      getSafeThreshold,
      getSafeNonce,
      chain,
      showError,
      showSuccess,
      userAddress,
      publicClient,
      contractNetworks,
      getSafeProviderAndSigner,
    ]
  )

  const handleRejectPendingTransaction = useCallback(
    async (tx: EthSafeTransaction, hash: string, safeAddress: string) => {
      const isTreasurySafe =
        safeAddress.toLowerCase() === treasurySafe?.toLowerCase()

      if (!isSafeReady && isTreasurySafe) {
        showError('Safe is not ready. Please wait...')
        return
      }

      if (
        !isTreasurySafe &&
        !userOperationalSafes.has(safeAddress.toLowerCase())
      ) {
        showError('You are not an owner of this Safe')
        return
      }

      if (!userAddress || !publicClient) {
        showError('Wallet not connected')
        return
      }

      let rejectionTxNonce: number | undefined
      try {
        setRejectingTxHash(hash)

        let rejectionTx: EthSafeTransaction
        let getTxHash: (safeTx: EthSafeTransaction) => Promise<string>

        if (isTreasurySafe) {
          const connectionData = await getSafeProviderAndSigner()
          if (!connectionData) {
            showError('Provider not available')
            return
          }

          const config = createConnectionConfig(
            connectionData.provider,
            connectionData.signer,
            safeAddress as `0x${string}`,
            contractNetworks
          )

          const kit = await Safe.init(config)
          const connectedKit = await kit.connect(config)

          rejectionTx = await connectedKit.createRejectionTransaction(
            tx.data.nonce
          )
          rejectionTx = await connectedKit.signTransaction(rejectionTx)

          const rejectionTxHash = await connectedKit.getTransactionHash(
            rejectionTx
          )
          getTxHash = async (_safeTx: EthSafeTransaction) => {
            void _safeTx
            return rejectionTxHash
          }
        } else {
          const connectionData = await getSafeProviderAndSigner()
          if (!connectionData) {
            showError('Provider not available')
            return
          }

          const config = createConnectionConfig(
            connectionData.provider,
            connectionData.signer,
            safeAddress as `0x${string}`,
            contractNetworks
          )

          const kit = await Safe.init(config)
          const connectedKit = await kit.connect(config)

          rejectionTx = await connectedKit.createRejectionTransaction(
            tx.data.nonce
          )
          rejectionTx = await connectedKit.signTransaction(rejectionTx)

          const rejectionTxHash = await connectedKit.getTransactionHash(
            rejectionTx
          )
          getTxHash = async (_safeTx: EthSafeTransaction) => {
            void _safeTx
            return rejectionTxHash
          }
        }

        const rejectionTxNonceValue = rejectionTx.data.nonce
        rejectionTxNonce = rejectionTxNonceValue
        setCreatingTransactions((prev) => {
          const newMap = new Map(prev)
          newMap.set(rejectionTxNonceValue, 'Reject')
          return newMap
        })

        if (chain?.id) {
          const result = await submitSafeTransactionToService(
            rejectionTx,
            safeAddress,
            chain.id,
            userAddress,
            getTxHash
          )

          if (result.success) {
            showSuccess('Reject transaction created successfully!')
            const currentNonce = await getSafeNonce()
            const treasuryResult = await fetchPendingTransactionsFromService(
              treasurySafe,
              chain.id,
              currentNonce ?? undefined
            )

            const allTransactions: PendingTransaction[] = []

            if (treasuryResult.success && treasuryResult.transactionsWithMeta) {
              const typePromises = treasuryResult.transactionsWithMeta.map(
                (txMeta) =>
                  getTransactionTypeCallback(txMeta.tx, treasurySafe).then(
                    (type) => ({
                      txMeta,
                      type,
                    })
                  )
              )
              const typedTxs = await Promise.all(typePromises)

              const treasuryTxs = typedTxs.map(({ txMeta, type }) => ({
                tx: txMeta.tx,
                hash: txMeta.safeTxHash,
                isReady:
                  txMeta.confirmationsCount >= txMeta.confirmationsRequired,
                type,
                safeAddress: treasurySafe,
              }))
              allTransactions.push(...treasuryTxs)
            }

            const previousTransactions = previousTransactionsRef.current
            if (!areTransactionsEqual(previousTransactions, allTransactions)) {
              setPendingTransactions(allTransactions)
              previousTransactionsRef.current = allTransactions

              removeFromCreatingTransactions(
                allTransactions.map((tx) => tx.tx.data.nonce)
              )
            } else {
              if (rejectionTxNonce !== undefined) {
                const rejectionTxInList = allTransactions.some(
                  (tx) =>
                    tx.tx.data.nonce === rejectionTxNonce &&
                    tx.type === 'Reject'
                )
                if (rejectionTxInList) {
                  removeFromCreatingTransactions(rejectionTxNonce)
                }
              }
            }
          } else {
            if (rejectionTxNonce !== undefined) {
              removeFromCreatingTransactions(rejectionTxNonce)
            }
            showError(result.error || 'Failed to reject transaction')
          }
        }
      } catch (error) {
        if (rejectionTxNonce !== undefined) {
          removeFromCreatingTransactions(rejectionTxNonce)
        }
        if (isUserRejectedError(error)) {
          setCancelledMessage(
            'You cancelled the rejection request in MetaMask.'
          )
          setShowCancelledModal(true)
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to reject transaction'
          )
        }
      } finally {
        setRejectingTxHash(null)
      }
    },
    [
      isSafeReady,
      treasurySafe,
      userOperationalSafes,
      chain,
      showError,
      showSuccess,
      userAddress,
      publicClient,
      contractNetworks,
      getSafeProviderAndSigner,
      getSafeNonce,
      getTransactionTypeCallback,
      areTransactionsEqual,
      removeFromCreatingTransactions,
    ]
  )

  const handleSimulatePendingTransaction = useCallback(
    async (tx: EthSafeTransaction, hash: string, safeAddress: string) => {
      if (!chain?.id || !publicClient) {
        showError('Chain or public client not available')
        return
      }

      try {
        setSimulatingTxHash(hash)
        const result = await simulateTransaction(
          tx,
          safeAddress,
          chain.id,
          publicClient,
          hash
        )

        setSimulationResults((prev) => ({
          ...prev,
          [hash]: {
            success: result.success,
            error: result.error,
          },
        }))

        if (!result.success) {
          const errorMsg = result.error || 'Failed to simulate transaction'
          showError(errorMsg)
        }
      } catch (error) {
        showError(
          error instanceof Error
            ? error.message
            : 'Failed to simulate transaction'
        )
      } finally {
        setSimulatingTxHash(null)
      }
    },
    [chain, publicClient, showError]
  )

  const handleSignPendingTransaction = useCallback(
    async (tx: EthSafeTransaction, hash: string, safeAddress: string) => {
      const isTreasurySafe =
        safeAddress.toLowerCase() === treasurySafe?.toLowerCase()

      if (!isSafeReady && isTreasurySafe) {
        showError('Safe is not ready. Please wait...')
        return
      }

      if (
        !isTreasurySafe &&
        !userOperationalSafes.has(safeAddress.toLowerCase())
      ) {
        showError('You are not an owner of this Safe')
        return
      }

      if (!userAddress || !publicClient) {
        showError('Wallet not connected')
        return
      }

      try {
        setSigningTxHash(hash)

        let signedTx: EthSafeTransaction
        let getTxHash: (safeTx: EthSafeTransaction) => Promise<string>

        if (isTreasurySafe) {
          signedTx = await signSafeTransaction(tx)
          getTxHash = getSafeTransactionHash
        } else {
          const connectionData = await getSafeProviderAndSigner()
          if (!connectionData) {
            showError('Provider not available')
            return
          }

          const config = createConnectionConfig(
            connectionData.provider,
            connectionData.signer,
            safeAddress as `0x${string}`,
            contractNetworks
          )

          const kit = await Safe.init(config)
          const connectedKit = await kit.connect(config)

          signedTx = await connectedKit.signTransaction(tx)

          const signedTxHash = await connectedKit.getTransactionHash(signedTx)
          getTxHash = async (_safeTx: EthSafeTransaction) => {
            void _safeTx
            return signedTxHash
          }
        }

        if (chain?.id && userAddress) {
          await submitSafeTransactionToService(
            signedTx,
            safeAddress,
            chain.id,
            userAddress,
            getTxHash
          )
        }

        const allTransactions: PendingTransaction[] = []

        const currentNonce = await getSafeNonce()
        const treasuryResult = await fetchPendingTransactionsFromService(
          treasurySafe,
          chain?.id || 0,
          currentNonce ?? undefined
        )
        if (treasuryResult.success && treasuryResult.transactionsWithMeta) {
          const typePromises = treasuryResult.transactionsWithMeta.map(
            (txMeta) =>
              getTransactionTypeCallback(txMeta.tx, treasurySafe).then(
                (type) => ({
                  txMeta,
                  type,
                })
              )
          )
          const typedTxs = await Promise.all(typePromises)

          const treasuryTxs = typedTxs.map(({ txMeta, type }) => ({
            tx: txMeta.tx,
            hash: txMeta.safeTxHash,
            isReady: txMeta.confirmationsCount >= txMeta.confirmationsRequired,
            type,
            safeAddress: treasurySafe,
          }))
          allTransactions.push(...treasuryTxs)
        }

        const previousTransactions = previousTransactionsRef.current
        if (!areTransactionsEqual(previousTransactions, allTransactions)) {
          setPendingTransactions(allTransactions)
          previousTransactionsRef.current = allTransactions
        }
        showSuccess('Transaction signed successfully!')
      } catch (error) {
        if (isUserRejectedError(error)) {
          setCancelledMessage(
            'You cancelled the signature request in MetaMask.'
          )
          setShowCancelledModal(true)
        } else {
          showError(
            error instanceof Error
              ? error.message
              : 'Failed to sign transaction'
          )
        }
      } finally {
        setSigningTxHash(null)
      }
    },
    [
      isSafeReady,
      treasurySafe,
      chain,
      signSafeTransaction,
      getSafeTransactionHash,
      getSafeNonce,
      getTransactionTypeCallback,
      showError,
      showSuccess,
      userAddress,
      publicClient,
      contractNetworks,
      userOperationalSafes,
      areTransactionsEqual,
      getSafeProviderAndSigner,
    ]
  )

  const { isLoading: isConfirmingExecute, isSuccess: isExecuteSuccess } =
    useWaitForTransactionReceipt({
      hash: executeTxHash && publicClient ? executeTxHash : undefined,
      query: { enabled: !!executeTxHash && !!publicClient && !!chain },
    })

  useEffect(() => {
    if (isExecuteSuccess && executeTxHash) {
      setExecuteTxHash(null)
      setExecutingTxHash(null)

      showSuccess('Transaction executed successfully!')

      if (isSafeReady && treasurySafe && chain) {
        const loadPendingTransactionsAfterExecute = async () => {
          try {
            const currentNonce = await getSafeNonce()
            const treasuryResult = await fetchPendingTransactionsFromService(
              treasurySafe,
              chain.id,
              currentNonce ?? undefined
            )

            const allTransactions: PendingTransaction[] = []

            if (treasuryResult.success && treasuryResult.transactionsWithMeta) {
              const typePromises = treasuryResult.transactionsWithMeta.map(
                (txMeta) =>
                  getTransactionTypeCallback(txMeta.tx, treasurySafe).then(
                    (type) => ({
                      txMeta,
                      type,
                    })
                  )
              )
              const typedTxs = await Promise.all(typePromises)

              const treasuryTxs = typedTxs.map(({ txMeta, type }) => ({
                tx: txMeta.tx,
                hash: txMeta.safeTxHash,
                isReady:
                  txMeta.confirmationsCount >= txMeta.confirmationsRequired,
                type,
                safeAddress: treasurySafe,
              }))
              allTransactions.push(...treasuryTxs)
            }

            const previousTransactions = previousTransactionsRef.current
            if (!areTransactionsEqual(previousTransactions, allTransactions)) {
              setPendingTransactions(allTransactions)
              previousTransactionsRef.current = allTransactions
            }

            const executedTxStillPending =
              executingTxNonceForOverlay !== null
                ? allTransactions.some(
                    (tx) => tx.tx.data.nonce === executingTxNonceForOverlay
                  )
                : false

            if (
              !executedTxStillPending &&
              executingTxNonceForOverlay !== null
            ) {
              setExecutingTxHashForOverlay(null)
              setExecutingTxNonceForOverlay(null)
              setShowExecuteOverlay(false)
            }
          } catch (error) {
            console.error('Failed to reload pending transactions:', error)
            setExecutingTxHashForOverlay(null)
            setExecutingTxNonceForOverlay(null)
            setShowExecuteOverlay(false)
          }
        }
        loadPendingTransactionsAfterExecute()

        // Dispatch event to trigger transaction history reload
        window.dispatchEvent(new CustomEvent('transaction-executed'))
      } else {
        setExecutingTxHashForOverlay(null)
        setExecutingTxNonceForOverlay(null)
        setShowExecuteOverlay(false)
      }
    }
  }, [
    isExecuteSuccess,
    executeTxHash,
    showSuccess,
    isSafeReady,
    treasurySafe,
    chain,
    getSafeNonce,
    getTransactionTypeCallback,
    areTransactionsEqual,
    executingTxHashForOverlay,
    executingTxNonceForOverlay,
  ])

  const handleCopyHash = useCallback(
    async (hash: string) => {
      try {
        await copyToClipboard(hash)
        showSuccess('Transaction hash copied!')
      } catch (error) {
        console.error('Failed to copy hash:', error)
        showError('Failed to copy transaction hash')
      }
    },
    [showSuccess, showError]
  )

  return (
    <>
      <PendingTransactions
        pendingTransactions={pendingTransactions}
        creatingTransactions={creatingTransactions}
        isLoading={isLoadingPendingTransactions}
        userAddress={userAddress}
        treasurySafe={treasurySafe}
        stngProxy={stngProxy}
        operationalSafeFactory={operationalSafeFactory}
        isMobileOrTablet={isMobileOrTablet}
        safeThreshold={safeThreshold}
        signingTxHash={signingTxHash}
        executingTxHash={executingTxHash}
        rejectingTxHash={rejectingTxHash}
        simulatingTxHash={simulatingTxHash}
        simulationResults={simulationResults}
        isConfirmingExecute={isConfirmingExecute}
        showExecuteOverlay={showExecuteOverlay}
        executingTxNonceForOverlay={executingTxNonceForOverlay}
        isSafeReady={isSafeReady}
        userOperationalSafes={userOperationalSafes}
        onRefresh={loadPendingTransactions}
        onSign={handleSignPendingTransaction}
        onExecute={handleExecutePendingTransaction}
        onSimulate={handleSimulatePendingTransaction}
        onReject={handleRejectPendingTransaction}
        onCopyHash={handleCopyHash}
      />
      <TransactionCancelledModal
        open={showCancelledModal}
        onClose={() => {
          setShowCancelledModal(false)
          setCancelledMessage('')
        }}
        message={cancelledMessage}
      />
    </>
  )
}
